"use client";

import React, { useState, useEffect, useCallback, ChangeEvent, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import ImageViewer from '@/components/ImageViewer';
import { Loader2, ImageOff, CheckCircle, Upload, Eye, X } from '@/components/icons';
import CloseButton, { CloseIcon } from '@/components/CloseButton';
import { deleteImageFromStorage } from '@/lib/supabase/images';
import { useToast } from '@/components/ui/use-toast';
import { getAllUserPhotos, uploadProductImage } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ImageManagerDialogProps {
  session: Session | null;
  isOpen: boolean;
  onClose: () => void;
  // onSelect ahora puede devolver una sola URL (para avatar) o un array de URLs (para productos)
  onSelect: (urls: string | string[]) => void; 
  allowUpload?: boolean;
  allowMultipleSelect?: boolean; // Nuevo prop para habilitar selección múltiple
  maxSelection?: number; // Nuevo prop para límite de selección
  initialSelectedUrls?: string[]; // URLs ya seleccionadas (para productos)
}

const ImageManagerDialog: React.FC<ImageManagerDialogProps> = ({ 
  session, 
  isOpen, 
  onClose, 
  onSelect, 
  allowUpload = false,
  allowMultipleSelect = false,
  maxSelection = 15,
  initialSelectedUrls = [],
}) => {
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(10);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [confirmDeleteUrl, setConfirmDeleteUrl] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const currentUserId = session?.user?.id;
  
  // Ref para almacenar la selección inicial solo cuando el diálogo se abre
  const initialSelectionRef = useRef<string[]>([]); 

  // Memoized function to fetch photos
  const fetchPhotos = useCallback(async (initialUrls: string[]) => {
    if (!currentUserId) {
      setPhotos([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const fetchedPhotos = await getAllUserPhotos(currentUserId);
      
      // Combinar fotos existentes con las seleccionadas inicialmente, asegurando unicidad
      const allUniquePhotos = Array.from(new Set([...fetchedPhotos, ...initialUrls]));
      
      // Ordenar las fotos para que las seleccionadas inicialmente aparezcan primero
      const sortedPhotos = allUniquePhotos.sort((a, b) => {
        const aSelected = initialUrls.includes(a);
        const bSelected = initialUrls.includes(b);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return b.localeCompare(a); // Ordenar por fecha de subida (más reciente primero)
      });

      setPhotos(sortedPhotos);
    } catch (error) {
      console.error("Error fetching user photos:", error);
      toast({
        title: "Error al cargar fotos",
        description: "No se pudieron cargar tus fotos. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [currentUserId, toast]);

  // Effect para inicializar y cargar datos SOLO cuando el diálogo se abre
  useEffect(() => {
    if (isOpen) {
      // 1. Capturar la selección inicial del prop (que puede ser inestable)
      initialSelectionRef.current = initialSelectedUrls;
      
      // 2. Inicializar la selección
      setSelectedPhotos(initialSelectionRef.current); 
    
      // 3. Cargar las fotos
        // reset visible count
        setVisibleCount(10);
        fetchPhotos(initialSelectionRef.current).then(() => {
          // nothing
        });
    }
  }, [isOpen, fetchPhotos]); // FIXED: Removed initialSelectedUrls dependency

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (target.scrollHeight - target.scrollTop - target.clientHeight < 200) {
        // near bottom - load more images
        setVisibleCount((v) => Math.min(v + 15, photos.length));
      }
    };

    // Open a confirmation dialog (app-styled) instead of native confirm()
    const handleDeletePhoto = (url: string) => {
      setConfirmDeleteUrl(url);
    };

    const performDeletePhoto = async (url: string) => {
        try {
          if (url === '__MULTI__') {
            // delete all selectedPhotos
            setIsDeleting('__MULTI__');
            const toDelete = [...selectedPhotos];
            for (const p of toDelete) {
              try {
                await deleteImageFromStorage(p);
              } catch (err) {
                console.error('Error deleting image in bulk:', err);
              }
            }
            setPhotos(prev => prev.filter(p => !toDelete.includes(p)));
            setSelectedPhotos([]);
            toast({ title: 'Imágenes eliminadas', description: `${toDelete.length} imagen(es) eliminada(s) de la biblioteca.` });
          } else {
            setIsDeleting(url);
            await deleteImageFromStorage(url);
            setPhotos(prev => prev.filter(p => p !== url));
            setSelectedPhotos(prev => prev.filter(p => p !== url));
            toast({ title: 'Imagen eliminada', description: 'La imagen fue eliminada de la biblioteca.' });
          }
        } catch (err) {
          console.error('Error deleting image:', err);
          toast({ title: 'Error', description: 'No se pudo eliminar la imagen.', variant: 'destructive' });
        } finally {
          setIsDeleting(null);
          setConfirmDeleteUrl(null);
        }
    };

  const handleToggleSelect = (url: string) => {
    if (allowMultipleSelect) {
      setSelectedPhotos(prev => {
        if (prev.includes(url)) {
          // Deseleccionar
          return prev.filter(u => u !== url);
        } else {
          // Seleccionar, verificando límite
          if (prev.length < maxSelection) {
            return [...prev, url];
          } else {
            toast({
              title: "Límite alcanzado",
              description: `Solo puedes seleccionar hasta ${maxSelection} imágenes.`,
              variant: "destructive",
            });
            return prev;
          }
        }
      });
    } else {
      // Selección única (para avatar)
      setSelectedPhotos(prev => prev[0] === url ? [] : [url]);
    }
  };

  const handleSelect = () => {
    if (allowMultipleSelect) {
      onSelect(selectedPhotos);
    } else if (selectedPhotos.length > 0) {
      onSelect(selectedPhotos[0]);
    }
    onClose();
  };

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!currentUserId || !event.target.files || event.target.files.length === 0) return;

    const files = Array.from(event.target.files);
    setIsUploading(true);
    const uploadedUrls: string[] = [];
    let uploadSuccessCount = 0;

    try {
      for (const file of files) {
        // Verificar si la subida excederá el límite de selección si estamos en modo múltiple
        if (allowMultipleSelect && selectedPhotos.length + uploadedUrls.length >= maxSelection) {
            toast({
                title: "Límite de subida",
                description: `Solo puedes subir hasta ${maxSelection - selectedPhotos.length} imágenes más.`,
                variant: "destructive",
            });
            break; // Detener la subida si se alcanza el límite
        }

        try {
          const imageUrl = await uploadProductImage(file, currentUserId);
          uploadedUrls.push(imageUrl);
          uploadSuccessCount++;
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
        }
      }

      if (uploadedUrls.length > 0) {
        // Añadir las nuevas imágenes al inicio de la lista de fotos disponibles
        setPhotos(prev => [...uploadedUrls, ...prev]);
        
        // Si estamos en modo múltiple, seleccionar automáticamente las nuevas subidas
        if (allowMultipleSelect) {
            setSelectedPhotos(prev => [...prev, ...uploadedUrls]);
        } else if (uploadedUrls.length > 0) {
            // Si es selección única, seleccionar la primera subida
            setSelectedPhotos([uploadedUrls[0]]);
        }
      } else if (files.length > 0) {
        toast({
          title: "Error en la subida",
          description: "No se pudo subir ninguna de las imágenes seleccionadas.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error during batch upload:", error);
      toast({
        title: "Error general de subida",
        description: "Ocurrió un error inesperado durante la subida.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Clear file input
      }
    }
  };

  const isSelected = (url: string) => selectedPhotos.includes(url);
  const selectionCount = selectedPhotos.length;
  const isSelectButtonDisabled = loading || isUploading || (allowMultipleSelect ? selectionCount === 0 : selectionCount !== 1);

  return (
    <>
      {/* Overlay backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-[9998] transition-opacity"
          onClick={onClose}
          style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed right-0 top-0 bottom-0 w-full lg:w-[400px] bg-background shadow-2xl z-[9999] flex flex-col',
          isOpen ? 'translate-x-0 opacity-100 pointer-events-auto' : 'translate-x-full opacity-0 pointer-events-none',
          'transition-all duration-300'
        )}
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground flex-shrink-0 h-16">
          <h2 className="text-lg font-semibold truncate">Biblioteca de Medios</h2>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground ml-2 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0" onScroll={handleScroll} ref={scrollContainerRef}>
          <div className="p-4">
            {loading ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {[...Array(12)].map((_, i) => (
                  <Skeleton key={i} className="aspect-square w-full rounded-md" />
                ))}
              </div>
            ) : photos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <ImageOff className="h-12 w-12 mb-4" />
                <p className="text-center text-sm">Aún no has subido ninguna foto.</p>
                {isUploading && (
                  <div className="mt-4 flex flex-col items-center">
                    <Loader2 className="h-8 w-8 animate-spin mb-2 text-primary" />
                    <span className="text-sm">Subiendo imagen...</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {isUploading && (
                  <div className="relative">
                    <div className="aspect-square w-full rounded-md flex items-center justify-center bg-muted animate-pulse">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  </div>
                )}
                {photos.slice(0, visibleCount).map((photoUrl) => (
                  <div key={photoUrl} className="relative">
                    <button
                      type="button"
                      className={cn(
                        "relative aspect-square w-full p-0 overflow-hidden rounded-md group focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer",
                        isSelected(photoUrl) && "ring-2 ring-primary ring-offset-2"
                      )}
                      onClick={() => handleToggleSelect(photoUrl)}
                      disabled={isUploading}
                    >
                      <img
                        src={photoUrl}
                        alt="Foto de la biblioteca"
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                      {isSelected(photoUrl) && (
                        <div className="absolute inset-0 bg-primary/50 flex items-center justify-center">
                          <CheckCircle className="h-8 w-8 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Selection Counter - Fixed */}
        {allowMultipleSelect && (
          <div className="px-4 py-2 bg-muted/50 text-center text-xs font-medium border-t flex-shrink-0">
            Seleccionadas: {selectionCount}/{maxSelection}
          </div>
        )}

        {/* Footer with Actions - Fixed */}
        <div className="border-t p-4 space-y-3 flex-shrink-0 bg-background">
          {allowUpload && (
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full h-[43px] rounded-full shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 text-sm font-semibold"
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Subir Nueva Imagen
                </>
              )}
            </Button>
          )}
          <input
            id="image-upload-gallery"
            type="file"
            accept="image/*"
            multiple={allowMultipleSelect}
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            disabled={isUploading || loading}
          />

          <div className="flex gap-2">
            <Button
              onClick={() => {
                if (selectionCount === 0) return;
                setViewerUrl(null);
                setViewerOpen(true);
              }}
              disabled={selectionCount === 0}
              className="w-full h-[43px] rounded-full shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 text-sm font-semibold"
              variant="outline"
            >
              <Eye className="h-4 w-4 mr-2" />
              Ver
            </Button>

            <Button
              onClick={() => {
                if (selectionCount === 0) return;
                setConfirmDeleteUrl('__MULTI__');
              }}
              disabled={selectionCount === 0 || isDeleting !== null}
              className="w-full h-[43px] rounded-full shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 text-sm font-semibold"
              variant="destructive"
            >
              <X className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>

          <Button
            onClick={handleSelect}
            disabled={isSelectButtonDisabled}
            className="w-full h-[43px] rounded-full shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 text-sm font-semibold"
          >
            Aceptar {allowMultipleSelect && `(${selectionCount})`}
          </Button>
        </div>
      </div>

      {/* Confirmation dialog for deletion */}
      <AlertDialog open={Boolean(confirmDeleteUrl)} onOpenChange={(open) => { if (!open) setConfirmDeleteUrl(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDeleteUrl === '__MULTI__' ? 'Eliminar imágenes' : 'Eliminar imagen'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDeleteUrl === '__MULTI__'
                ? `¿Eliminar ${selectionCount} imagen(es) de tu biblioteca? Esta acción no se puede deshacer.`
                : '¿Eliminar esta imagen de tu biblioteca? Esta acción no se puede deshacer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting !== null}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDeleteUrl && performDeletePhoto(confirmDeleteUrl)}
              disabled={isDeleting !== null}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                confirmDeleteUrl === '__MULTI__' ? `Eliminar (${selectionCount})` : 'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image preview modal */}
      <ImageViewer imageUrl={viewerUrl} imageUrls={selectedPhotos.length > 0 ? selectedPhotos : undefined} startIndex={0} isOpen={viewerOpen} onClose={() => setViewerOpen(false)} />
    </>
  );
};

export default ImageManagerDialog;